import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Box, Typography } from '@mui/material';
import { useResponsive } from 'src/hooks/useResponsive';
import { usePaymentProvider } from 'src/integrations/PaymentProvider/hooks/usePaymentProvider';
import { OrderPaymentType } from 'components/order/model/Order';
import { UserCard } from 'components/user/model/User';
import { CONTENT_MAX_WIDTH } from 'config/constants';
import { useAuth } from 'lib/useAuth';
import { getUserCard } from 'store/auth/authSlice';
import { setLastRequest } from 'store/request/requestSlice';
import { ApplicationState } from 'store/store';
import { PayBaseModal } from '../modals/PayBaseModal';

const PREFIX = 'PayAddCardDialog';

const classes = {
    dialog: `${PREFIX}dialog`,
    button: `${PREFIX}button`,
    buttonContainer: `${PREFIX}buttonContainer`
};

const StyledPayBaseModal = styled(PayBaseModal)(({ theme }) => ({
    [`& .${classes.button}`]: {
        textTransform: 'none',
        padding: theme.spacing(1.5, 2),
        fontWeight: 700
    },

    [`& .${classes.buttonContainer}`]: {
        padding: 0,
        marginTop: theme.spacing(3)
    }
}));

interface PayAddCardDialogProps {
    handleCreateOrder: (paymentType: OrderPaymentType, selectedCard: UserCard | null) => () => void;
    open: boolean;
    onClose: () => void;
}

export const PayAddCardDialog: React.FC<React.PropsWithChildren<PayAddCardDialogProps>> = ({
    handleCreateOrder,
    open,
    onClose
}) => {
    const { isDesktop } = useResponsive();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    const dispatch = useAppDispatch();

    const handleCardAdded = React.useCallback(() => {
        if (user && user.id) {
            dispatch(getUserCard(user.id))
                .unwrap()
                .then(userCard => {
                    if (userCard) {
                        const requestFunction = handleCreateOrder(OrderPaymentType.CARD_ON_FILE, userCard);
                        dispatch(setLastRequest(requestFunction));
                        requestFunction();
                        onClose();
                    }
                });
        }
    }, [user, dispatch, handleCreateOrder, onClose]);
    const { isProviderLoading, addCardComponent } = usePaymentProvider(
        { type: 'cardOnFile' },
        {
            onCardAdded: handleCardAdded,
            classes: { button: classes.button, buttonContainer: classes.buttonContainer }
        }
    );
    const dialogStyles = React.useMemo(() => {
        if (isDesktop) {
            return {
                maxWidth: CONTENT_MAX_WIDTH,
                width: CONTENT_MAX_WIDTH,
                borderBottomRightRadius: 'shape.borderRadius',
                borderBottomLeftRadius: 'shape.borderRadius'
            };
        }
        return undefined;
    }, [isDesktop]);
    if (!settings) {
        return null;
    }
    return (
        <StyledPayBaseModal open={open} onClose={onClose} paperSx={dialogStyles}>
            <Box px={1} py={1.5}>
                <Typography sx={{ fontWeight: 700, fontSize: '24px', mb: 1 }} align="center">
                    {t('PAT_QUICKPAY_ADD_CARD_DIALOG_TITLE')}
                </Typography>
                {!isProviderLoading && !!addCardComponent && open && addCardComponent}
            </Box>
        </StyledPayBaseModal>
    );
};
